import { TranslationNamespaces } from '~/lib/constants';
import { ProductShopAddress } from '../../adapters/product-types';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { DropOffMoreInfo } from '~/components/basket-summary/basket/drop-off-more-info';
import { P2GGrid, P2GIcon, P2GText, SvgMapMarker, SvgPrinter, SvgShop } from '@parcel2go/core-ui';
import { useDistance } from '@parcel2go/npm-p2g-app';
import { BasketSections } from '~/lib/types/enums';

type ProductItemShopProps = {
  shop: ProductShopAddress;
  quoteOffersPrintInStore?: boolean;
  type: BasketSections;
};

export function ProductItemShop({ shop, quoteOffersPrintInStore, type }: ProductItemShopProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { toDistance } = useDistance();
  const [open, setOpen] = useState(false);
  const hasPrintInStore = shop.printInStoreAvailable && quoteOffersPrintInStore === true;

  const isOrigin = type === BasketSections.ORIGIN;

  const distance = useMemo(() => {
    if (!shop.distance) {
      return null;
    }
    return toDistance(shop.distance);
  }, [shop.distance, toDistance]);

  return (
    <>
      <P2GGrid dataTestId="basket-item-details-container" container direction="row" spacing={2} wrap="nowrap">
        <P2GGrid item>
          <P2GIcon size="sm" palette="success" onClick={() => setOpen(true)}>
            {isOrigin ? <SvgMapMarker /> : <SvgShop />}
          </P2GIcon>
        </P2GGrid>
        <P2GGrid item>
          <P2GGrid container direction="column" spacing={1}>
            <P2GGrid item xs={12}>
              <P2GText color="textSecondary" variant="body1" fontWeight="bold">
                {isOrigin
                  ? t('BASKET_CHOSEN_DROP_OFF_SHOP_TITLE', {
                      defaultValue: 'Chosen Drop Off Location:'
                    })
                  : t('BASKET_CHOSEN_COLLECTION_SHOP_TITLE', {
                      defaultValue: 'Chosen Collection Location:'
                    })}
              </P2GText>
            </P2GGrid>
            <P2GGrid item xs={12}>
              <P2GText variant="body2">
                {distance
                  ? t('PRODUCT_CHOSEN_DROP_OFF_SHOP_ADDRESS_AND_DISTANCE', {
                      defaultValue: '{{address}} - Distance {{distance}}',
                      address: [shop.name, shop.address2, shop.postcode].filter(Boolean).join(', '),
                      distance: distance
                    })
                  : t('PRODUCT_CHOSEN_DROP_OFF_SHOP_ADDRESS', {
                      defaultValue: '{{address}}',
                      address: [shop.name, shop.address2, shop.postcode].filter(Boolean).join(', ')
                    })}
              </P2GText>
            </P2GGrid>
            {hasPrintInStore && isOrigin && (
              <P2GGrid item xs={12}>
                <P2GGrid container spacing={1} direction="row" wrap="nowrap">
                  <P2GGrid item>
                    <P2GIcon size="xs" palette="success">
                      <SvgPrinter />
                    </P2GIcon>
                  </P2GGrid>
                  <P2GGrid item>
                    <P2GText variant="body2">
                      {t('CONFIRM_PRINT_AVAILABLE', { defaultValue: 'Print in Shop - Available' })}
                    </P2GText>
                  </P2GGrid>
                </P2GGrid>
              </P2GGrid>
            )}
          </P2GGrid>
        </P2GGrid>
      </P2GGrid>
      <DropOffMoreInfo open={open} onClose={() => setOpen(false)} />
    </>
  );
}
